/**
 * BL-1071 (ADR 034) — the parent-auction **ceiling**: the last app-layer lot-rule
 * holdout converged into the shared lifecycle rule core. Answers "which lot
 * statuses may an operator manually set, given the parent auction's status?" —
 * a lot's status may not exceed its parent auction's phase.
 *
 * Pure IsomorphicService (ADR 010 / sharing.md §3): no I/O, no auth, no writes,
 * client-safe (NO `import 'server-only'`). Sibling of `can-batch-apply.ts` /
 * `lot-field-editability.ts`. Moved verbatim (behavior-preserving) from
 * admin-web's `lot-status-validation.ts` so the single-edit form (`ItemForm`),
 * the lot-write routes, and the batch verdict all read ONE ceiling — they can't
 * drift. This IS the single source `canBatchApply`'s `auctionAllowsTargetStatus`
 * input is computed from (matrix note 3); `canBatchApply` keeps taking the
 * pre-computed boolean, so this predicate composes with it rather than inlining.
 *
 * Keyed on the canonical ADR-023 auction phase — {@link AuctionPhaseKind}
 * (auction axis) → the settable lot labels — so no new vocabulary is introduced.
 * (BL-1285 re-keyed this off the legacy 6-value `AuctionStatusLabel`. The
 * verdicts are unchanged: `paused` maps to the same set the legacy label gave a
 * paused auction, which derived `Active`.)
 */
import type { AuctionPhaseKind, LotStatusLabel } from './phase-tokens';

/**
 * The lot statuses an operator may manually set. Only `draft` / `ready` are
 * manual targets — `active` is auto-promoted (see admin-web
 * `resolveAutoPromoteOnSave`) and every terminal status comes from a clerk /
 * post-sale disposition flow, never a direct status edit. Tied to the core
 * {@link LotStatusLabel} via `Extract` so dropping `draft`/`ready` from the phase
 * vocabulary breaks here (drift guard). Assignable to admin-web's narrower
 * `InventoryStatus` too, so client consumers (`ItemForm`) index cleanly.
 */
export type ManuallySettableLotStatus = Extract<LotStatusLabel, 'draft' | 'ready'>;

/**
 * Maps each parent-auction phase to the lot statuses a user can manually set.
 * When the auction is `active`, `ready` is allowed as input but auto-promotes to
 * `active` (admin-web `resolveAutoPromoteOnSave`). `sold`/`unsold` are excluded —
 * they're set exclusively by clerk disposition. Only a soft-deleted auction
 * narrows to draft-only.
 *
 * BL-1291: `accepting-pre-bids` gets the same set as `ready` — verdict-
 * preserving on the same reasoning: before the rung existed, an auction with
 * pre-bidding lots derived `ready` and landed on that row. Whether open
 * pre-bids should narrow lot cataloguing is a separate product decision.
 *
 * BL-1285: `paused` gets the same set as `active`. That is verdict-preserving,
 * not a new decision — before this card a paused auction derived the legacy
 * `Active` label and landed on this exact row. Pausing halts BIDDING; it was
 * never a lot-cataloguing restriction.
 */
const MANUALLY_SETTABLE_LOT_STATUSES: Record<AuctionPhaseKind, ManuallySettableLotStatus[]> = {
  draft: ['draft', 'ready'],
  'accepting-pre-bids': ['draft', 'ready'],
  ready: ['draft', 'ready'],
  active: ['draft', 'ready'],
  paused: ['draft', 'ready'],
  closed: ['draft', 'ready'],
  deleted: ['draft'],
};

/**
 * Returns the lot statuses a user can manually set, given the auction's current
 * status. Accepts `undefined`/missing/unknown auction status (returns the safe
 * fallback `['draft']`) so callers can pass through optional auction lookups
 * without conditional juggling.
 *
 * @param auctionStatus The parent auction's current phase (e.g. 'draft',
 *   'ready'). Tolerant of any string / null / undefined — an unrecognized value
 *   falls back to the draft-only set.
 * @returns The valid lot statuses for manual selection.
 * @sideEffect none — pure.
 */
export function getManuallySettableLotStatuses(
  auctionStatus: string | undefined | null,
): ManuallySettableLotStatus[] {
  if (!auctionStatus) return ['draft'];
  return MANUALLY_SETTABLE_LOT_STATUSES[auctionStatus as AuctionPhaseKind] ?? ['draft'];
}

/**
 * Checks whether a requested lot status is valid for manual setting given the
 * auction status — the ceiling gate every lot-write path enforces.
 *
 * @param lotStatus The requested lot status.
 * @param auctionStatus The parent auction's current phase.
 * @returns true if the status change is allowed under the ceiling.
 * @sideEffect none — pure.
 */
export function isLotStatusAllowed(lotStatus: string, auctionStatus: string | undefined | null): boolean {
  return getManuallySettableLotStatuses(auctionStatus).includes(lotStatus as ManuallySettableLotStatus);
}
